"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SimpleTabs, type SimpleTab } from "./SimpleTabs";
import { DataTable, type Column } from "./DataTable";
import { useERP, formatBDT, formatNum } from "@/lib/erp/store";
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Star, Brain, Bell, Sparkles, ArrowRight } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

export function ExecutiveControlTower() {
  const erp = useERP();
  const [tab, setTab] = React.useState("alerts");
  const tabs: SimpleTab[] = [
    { value: "alerts", label: "Alerts", icon: Bell },
    { value: "approvals", label: "Approvals", icon: CheckCircle2 },
    { value: "performers", label: "Top Performers", icon: Star },
    { value: "ai", label: "AI Insights", icon: Brain },
  ];

  // Live KPIs
  const netSales = erp.salesInvoices.reduce((s, i) => s + i.amount, 0);
  const outstandingAR = erp.customers.reduce((s, c) => s + c.outstanding, 0);
  const cashCollected = erp.customerReceipts.filter(r => r.status === "Reconciled").reduce((s, r) => s + r.amount, 0);
  const openPOValue = erp.purchaseOrders.filter(p => p.status === "Approved" || p.status === "Pending Approval" || p.status === "Partially Received").reduce((s, p) => s + p.total, 0);

  // Chart data
  const salesTrend = [
    { name: "Mon", sales: 220000, target: 250000 },
    { name: "Tue", sales: 280000, target: 250000 },
    { name: "Wed", sales: 245000, target: 250000 },
    { name: "Thu", sales: 310000, target: 250000 },
    { name: "Fri", sales: 380000, target: 280000 },
    { name: "Sat", sales: 420000, target: 300000 },
    { name: "Sun", sales: 290000, target: 220000 },
  ];
  const zoneData = erp.customers.reduce((acc, c) => {
    const z = c.zone; const ex = acc.find(x => x.name === z);
    if (ex) ex.value += c.outstanding; else acc.push({ name: z, value: c.outstanding });
    return acc;
  }, [] as { name: string; value: number }[]);
  const topProducts = erp.products.slice(0, 5).map((p, i) => ({ name: p.name.slice(0, 14), qty: 200 - i * 30, revenue: (200 - i * 30) * p.salePrice }));
  const COLORS = ["#f59e0b", "#10b981", "#e11d48", "#7c3aed", "#06b6d4"];

  const alerts = [
    { id: "a1", severity: "High", title: "Customer Padma Bazar blocked due to credit overflow", module: "Credit Control", time: "10 min ago" },
    { id: "a2", severity: "Medium", title: "Tea Grade A 500g below reorder point", module: "Inventory", time: "25 min ago" },
    { id: "a3", severity: "High", title: "PO-2026-1023 pending approval for 24+ hours", module: "Procurement", time: "1 hour ago" },
    { id: "a4", severity: "Low", title: "Driver Rakib Hossain on field > 6 hours", module: "Tracking", time: "2 hours ago" },
    { id: "a5", severity: "Medium", title: "VAT return due in 3 days", module: "Finance", time: "3 hours ago" },
  ];
  const pendingApprovals = erp.approvals.filter(a => a.status === "Pending");
  const performers = erp.employees.slice(0, 6).map((e, i) => ({
    id: e.id, rank: i + 1, name: e.name, dept: e.department, role: e.designation,
    score: 92 - i * 5, sales: 1200000 - i * 180000, target: 1000000,
  }));

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold sm:text-2xl">Executive Control Tower</h1>
        <p className="text-muted-foreground mt-1 text-sm">Real-time unified view of business KPIs, alerts and approvals.</p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader><CardTitle className="text-muted-foreground flex items-center justify-between text-xs font-medium uppercase tracking-wide">
            Net Sales <TrendingUp className="text-primary size-4" />
          </CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{formatBDT(netSales)}</div>
            <p className="text-muted-foreground mt-1 text-xs">▲ 12.4% vs last week</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-muted-foreground flex items-center justify-between text-xs font-medium uppercase tracking-wide">
            Outstanding AR <TrendingDown className="text-destructive size-4" />
          </CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-destructive">{formatBDT(outstandingAR)}</div>
            <p className="text-muted-foreground mt-1 text-xs">▲ 4.2% vs last week</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-muted-foreground flex items-center justify-between text-xs font-medium uppercase tracking-wide">
            Cash Collected <TrendingUp className="text-primary size-4" />
          </CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold text-primary">{formatBDT(cashCollected)}</div>
            <p className="text-muted-foreground mt-1 text-xs">▲ 18.7% vs last week</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-muted-foreground flex items-center justify-between text-xs font-medium uppercase tracking-wide">
            Open PO Value <AlertTriangle className="text-amber-500 size-4" />
          </CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{formatBDT(openPOValue)}</div>
            <p className="text-muted-foreground mt-1 text-xs">5 active POs</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Sales Trend (Last 7 days)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={salesTrend} margin={{ left: -16, right: 8, top: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#9ca3af" fontSize={11} />
                <YAxis stroke="#9ca3af" fontSize={11} tickFormatter={v => `${(v / 1000).toFixed(0)}K`} />
                <Tooltip formatter={(v: number) => formatBDT(v)} />
                <Area type="monotone" dataKey="sales" stroke="#f59e0b" fill="url(#g1)" strokeWidth={2} />
                <Area type="monotone" dataKey="target" stroke="#94a3b8" fill="transparent" strokeDasharray="4 4" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>AR by Zone</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={zoneData} margin={{ left: -16, right: 8, top: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" stroke="#9ca3af" fontSize={10} />
                <YAxis stroke="#9ca3af" fontSize={11} tickFormatter={v => `${(v / 100000).toFixed(0)}L`} />
                <Tooltip formatter={(v: number) => formatBDT(v)} />
                <Bar dataKey="value" fill="#7c3aed" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Top 5 Products by Revenue</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={topProducts} layout="vertical" margin={{ left: 8, right: 8, top: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" stroke="#9ca3af" fontSize={11} tickFormatter={v => `${(v / 1000).toFixed(0)}K`} />
                <YAxis type="category" dataKey="name" stroke="#9ca3af" fontSize={10} width={100} />
                <Tooltip formatter={(v: number) => formatBDT(v)} />
                <Bar dataKey="revenue" fill="#10b981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardTitle className="px-6 pt-6">Customer Distribution</CardTitle>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={zoneData.slice(0, 5)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {zoneData.slice(0, 5).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v: number) => formatBDT(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <SimpleTabs value={tab} onValueChange={setTab} tabs={tabs} />

      {tab === "alerts" && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Bell className="size-5" /> Active Alerts</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alerts.map(a => (
                <div key={a.id} className="flex items-center gap-3 rounded-md border p-3">
                  <Badge variant={a.severity === "High" ? "destructive" : a.severity === "Medium" ? "secondary" : "outline"}>{a.severity}</Badge>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{a.title}</p>
                    <p className="text-muted-foreground text-xs">{a.module} · {a.time}</p>
                  </div>
                  <Button size="sm" variant="outline">Resolve</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {tab === "approvals" && (
        <Card>
          <CardHeader><CardTitle>Pending Approvals ({pendingApprovals.length})</CardTitle></CardHeader>
          <CardContent>
            <DataTable
              columns={[
                { key: "approvalNumber", header: "AP #", render: r => <span className="font-mono text-xs">{r.approvalNumber}</span> },
                { key: "entityType", header: "Type", sortable: true },
                { key: "requestedBy", header: "Requested By" },
                { key: "amount", header: "Amount", align: "right", sortable: true, render: r => formatBDT(r.amount) },
                { key: "priority", header: "Priority", render: r => <Badge variant={r.priority === "High" ? "destructive" : r.priority === "Medium" ? "secondary" : "outline"}>{r.priority}</Badge> },
                { key: "status", header: "Status", render: r => <Badge variant="secondary">{r.status}</Badge> },
              ] as Column<typeof pendingApprovals[number]>[]}
              data={pendingApprovals}
              rowKey={r => r.id}
              searchKeys={["approvalNumber", "entityType", "requestedBy"]}
            />
          </CardContent>
        </Card>
      )}

      {tab === "performers" && (
        <Card>
          <CardHeader><CardTitle>Top Performers This Month</CardTitle></CardHeader>
          <CardContent>
            <DataTable
              columns={[
                { key: "rank", header: "#", render: r => <span className="text-lg">{r.rank}</span> },
                { key: "name", header: "Employee", sortable: true, render: r => <span className="font-medium">{r.name}</span> },
                { key: "role", header: "Role" },
                { key: "dept", header: "Department" },
                { key: "score", header: "Score", align: "right", sortable: true, render: r => <span className="text-primary font-semibold">{r.score}</span> },
                { key: "sales", header: "Sales", align: "right", render: r => formatBDT(r.sales) },
                { key: "target", header: "vs Target", align: "right", render: r => <span className={r.sales >= r.target ? "text-primary" : "text-destructive"}>{((r.sales / r.target) * 100).toFixed(0)}%</span> },
              ] as Column<(typeof performers)[number]>[]}
              data={performers}
              rowKey={r => r.id}
            />
          </CardContent>
        </Card>
      )}

      {tab === "ai" && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Sparkles className="text-primary size-5" /> AI Recommendations</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { t: "Increase stock for Tea Grade A 500g", d: "Demand forecast predicts 35% spike next 2 weeks. Order 1,200 units from Hill View now.", confidence: 92 },
                  { t: "Extend credit to Karim Enterprise", d: "Excellent payment history. Recommend lifting limit from ৳3M to ৳4M.", confidence: 88 },
                  { t: "Bundle Sunflower Oil + Sugar Pack", d: "High co-purchase pattern detected. Suggested bundle discount of 5%.", confidence: 81 },
                  { t: "Reschedule ACI Foods delivery", d: "Lead time variance 1.8 days. Buffer recommended.", confidence: 76 },
                ].map((rec, i) => (
                  <div key={i} className="rounded-md border p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold">{rec.t}</p>
                      <Badge variant="secondary">{rec.confidence}%</Badge>
                    </div>
                    <p className="text-muted-foreground mt-1 text-xs">{rec.d}</p>
                    <Button size="sm" variant="ghost" className="mt-2 h-7 px-2 text-xs">Apply <ArrowRight className="size-3" /></Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Brain className="size-5" /> Anomaly Detection</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { t: "Unusual discount on SO-2026-8821", d: "15% discount exceeds peer average by 7%. Manual review advised.", severity: "warning" },
                  { t: "GRN rejection rate spike", d: "Bombay Sweets showing 4.2% rejection vs 0.8% baseline.", severity: "warning" },
                  { t: "Cash receipt velocity down", d: "Last 7 days collection -28% vs 30-day average.", severity: "critical" },
                  { t: "Margin compression detected", d: "Tea category margin dropped from 33% to 28%.", severity: "info" },
                ].map((a, i) => (
                  <div key={i} className="rounded-md border p-3">
                    <div className="flex items-center gap-2">
                      <span className={`size-2 rounded-full ${a.severity === "critical" ? "bg-red-500" : a.severity === "warning" ? "bg-amber-500" : "bg-blue-500"}`} />
                      <p className="text-sm font-semibold">{a.t}</p>
                    </div>
                    <p className="text-muted-foreground mt-1 text-xs">{a.d}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
